import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, errorMessage } from "../api/client";
import { ui } from "../lib/ui";
import type { PcStore, User, VersionInfo } from "../types";
import { AddStoreDialog } from "./AddStoreDialog";

const ITEM =
  "flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-left text-[13px] text-text transition-colors duration-100 hover:bg-bg disabled:cursor-default disabled:opacity-60";
const MENU = `${ui.card} z-50 w-72 p-1.5 shadow-[0_8px_24px_-4px_rgb(16_24_40_/_0.18)]`;

/** The header's gear: a menu of what applies to this console as a whole.
 *
 *  On a branch PC: **Switch store** — a submenu of every store this PC
 *  knows (hub stores it has a token for, local stores an operator named),
 *  the current one ticked, and "Add new store…". Switching re-scopes the
 *  whole console (numbers, floor plan, cameras, staff, runs) to that store
 *  and points the hub agent at it. **Database** and **Logs** open this PC's
 *  raw tables, and its log files and hub traffic. On the hub: who is signed
 *  in, and Sign out. The theme toggle stays its own button. */
export function SettingsMenu({
  info,
  user,
  onSignOut,
  onStoreChanged,
}: {
  info: VersionInfo;
  user: User | null;
  onSignOut: () => void;
  /** After a switch or an add: the console re-reads what it is. */
  onStoreChanged: () => void;
}) {
  const branchPc = info.mode === "branch";
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [storesOpen, setStoresOpen] = useState(false);
  const [stores, setStores] = useState<PcStore[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setStoresOpen(false);
    setError(null);
  }, []);

  // Outside click and Escape close the menu, as a native menu would.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const goTo = (path: string) => {
    close();
    navigate(path);
  };

  const loadStores = useCallback(() => {
    setStores(null);
    api
      .pcStores()
      .then(setStores)
      .catch((err: unknown) => setError(errorMessage(err)));
  }, []);

  const openStores = () => {
    const next = !storesOpen;
    setStoresOpen(next);
    if (next) loadStores();
  };

  const switchTo = async (store: PcStore) => {
    if (store.active) {
      close();
      return;
    }
    setBusy(store.id);
    setError(null);
    try {
      await api.activatePcStore(store.id);
      close();
      onStoreChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        className={`${ui.btnQuiet} px-2.5! text-muted! ${open ? ui.btnSelected : ""}`}
        onClick={() => (open ? close() : setOpen(true))}
        title="Settings"
        aria-label="Settings"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <GearIcon />
      </button>

      {open && (
        <div className={`${MENU} absolute right-0 top-full mt-1.5`} role="menu">
          {branchPc && (
            <div className="relative">
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={storesOpen}
                className={`${ITEM} ${storesOpen ? "bg-bg" : ""}`}
                onClick={openStores}
              >
                <StoreIcon />
                <span className="flex-1">Switch store</span>
                <span className="text-muted" aria-hidden="true">
                  {storesOpen ? "▾" : "▸"}
                </span>
              </button>
              {/* The submenu opens in place rather than flying out: the
                  header wraps on narrow panes and a flyout would leave
                  the viewport. */}
              {storesOpen && (
                <div
                  role="menu"
                  className="mt-1 rounded-md border border-border bg-bg/60 p-1"
                >
                  <p className={`${ui.label} px-3 pb-1 pt-1.5`}>
                    {info.hub?.url ? "Stores on this PC" : "Local stores on this PC"}
                  </p>
                  {error && (
                    <p className={`${ui.notice} ${ui.noticeError} mx-1.5 mb-1.5 px-2.5 py-1.5`}>
                      {error}
                    </p>
                  )}
                  {stores === null && !error && (
                    <p className="px-3 py-2 text-[13px] text-muted">Loading…</p>
                  )}
                  {stores?.length === 0 && (
                    <p className="px-3 py-2 text-[13px] text-muted">
                      No store yet — add one below.
                    </p>
                  )}
                  {stores?.map((store) => (
                    <button
                      key={store.id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={store.active}
                      className={ITEM}
                      disabled={busy !== null}
                      onClick={() => switchTo(store)}
                      title={store.id}
                    >
                      <span className="w-4 text-center text-accent" aria-hidden="true">
                        {busy === store.id ? "…" : store.active ? "✓" : ""}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={`block truncate ${store.active ? "font-semibold" : ""}`}>
                          {store.name}
                        </span>
                        <span className="block truncate text-xs text-muted">
                          {store.placeholder
                            ? "Not named yet"
                            : store.local
                              ? "Local store · stays on this PC"
                              : [store.governorate, store.area].filter(Boolean).join(" › ")}
                        </span>
                      </span>
                    </button>
                  ))}
                  <div className="my-1.5 border-t border-border" />
                  <button
                    type="button"
                    role="menuitem"
                    className={`${ITEM} text-accent`}
                    onClick={() => {
                      close();
                      setAdding(true);
                    }}
                  >
                    <span className="w-4 text-center" aria-hidden="true">
                      +
                    </span>
                    Add new store…
                  </button>
                </div>
              )}
              <div className="my-1.5 border-t border-border" />
              <button type="button" role="menuitem" className={ITEM} onClick={() => goTo("/database")}>
                <DatabaseIcon />
                Database
              </button>
              <button type="button" role="menuitem" className={ITEM} onClick={() => goTo("/logs")}>
                <LogsIcon />
                Logs
              </button>
            </div>
          )}

          {user && (
            <>
              {branchPc && <div className="my-1.5 border-t border-border" />}
              <p className="px-3 pb-1 pt-1.5 text-xs text-muted">
                Signed in as <span className="font-semibold text-text">{user.username}</span>
              </p>
              <button
                type="button"
                role="menuitem"
                className={ITEM}
                onClick={() => {
                  close();
                  onSignOut();
                }}
              >
                <SignOutIcon />
                Sign out
              </button>
            </>
          )}

          {!branchPc && !user && (
            <p className="px-3 py-2 text-[13px] text-muted">Nothing to set up here.</p>
          )}
        </div>
      )}

      {adding && (
        <AddStoreDialog
          hubUrl={info.hub?.url ?? null}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            onStoreChanged();
          }}
        />
      )}
    </div>
  );
}

function GearIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
  );
}

function StoreIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-muted"
    >
      <path d="M3 9l1.5-5h15L21 9" />
      <path d="M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0" />
      <path d="M5 11v9h14v-9" />
      <path d="M10 20v-5h4v5" />
    </svg>
  );
}

function DatabaseIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-muted"
    >
      <ellipse cx="12" cy="5" rx="8" ry="3" />
      <path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
      <path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
    </svg>
  );
}

function LogsIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-muted"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h8" />
      <path d="M8 17h8" />
    </svg>
  );
}

function SignOutIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-muted"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}
